/*
 * Collection & search facets: progressive enhancement for snippets/facets.liquid.
 * Loaded with `defer` by sections/collection.liquid and sections/search.liquid.
 * The pure helpers below are also exported for Node unit tests
 * (tests/unit/facets.test.cjs); nothing touches the DOM when loaded in Node.
 */
(function () {
  'use strict';

  const PRICE_PRESET_PARAM = 'price_preset';
  const PRICE_MIN_PARAM = 'filter.v.price.gte';
  const PRICE_MAX_PARAM = 'filter.v.price.lte';
  const DROPPED_PARAMS = ['page', 'section_id', 'sections'];
  const URL_BASE = 'https://facets.invalid';

  // ---- Pure helpers (unit-tested) ----

  /** ':99.99' -> { gte: null, lte: '99.99' }. Dollars, as Shopify reads them. '' / ':' / malformed -> null. */
  function parsePricePreset(value) {
    if (typeof value !== 'string') return null;
    const parts = value.split(':');
    if (parts.length !== 2) return null;
    const gte = parts[0].trim();
    const lte = parts[1].trim();
    if (gte === '' && lte === '') return null;
    if (gte !== '' && !Number.isFinite(Number(gte))) return null;
    if (lte !== '' && !Number.isFinite(Number(lte))) return null;
    return { gte: gte === '' ? null : gte, lte: lte === '' ? null : lte };
  }

  /**
   * Serialises form entries (e.g. `new FormData(form)`) to a query string.
   * Repeated keys are kept (OR within a group), empty values and paging/section
   * params are dropped, and the last `price_preset` entry replaces any
   * filter.v.price.gte/lte pair.
   */
  function buildQuery(entries) {
    const params = new URLSearchParams();
    let preset;
    for (const [key, raw] of entries) {
      if (typeof raw !== 'string') continue;
      const value = raw.trim();
      if (value === '' || DROPPED_PARAMS.includes(key)) continue;
      if (key === PRICE_PRESET_PARAM) {
        preset = parsePricePreset(value);
        continue;
      }
      params.append(key, value);
    }
    if (preset !== undefined) {
      params.delete(PRICE_MIN_PARAM);
      params.delete(PRICE_MAX_PARAM);
      if (preset && preset.gte !== null) params.append(PRICE_MIN_PARAM, preset.gte);
      if (preset && preset.lte !== null) params.append(PRICE_MAX_PARAM, preset.lte);
    }
    return params.toString();
  }

  function buildUrl(path, entries) {
    const query = buildQuery(entries);
    return query ? path + '?' + query : path;
  }

  /** Returns path + query (+ hash) with the named params removed. Accepts absolute or relative URLs. */
  function removeParams(url, names) {
    const parsed = new URL(url, URL_BASE);
    names.forEach((name) => parsed.searchParams.delete(name));
    const query = parsed.searchParams.toString();
    return parsed.pathname + (query ? '?' + query : '') + parsed.hash;
  }

  /** The Section Rendering API URL for a page URL. */
  function withSectionId(url, sectionId) {
    const parsed = new URL(url, URL_BASE);
    parsed.searchParams.set('section_id', sectionId);
    return parsed.pathname + '?' + parsed.searchParams.toString();
  }

  /** Reads the results count that snippets/facets.liquid prints as data-results-count. */
  function readResultsCount(html) {
    const match = /data-results-count="(\d+)"/.exec(html);
    return match ? Number(match[1]) : null;
  }

  function viewLabel(count) {
    if (typeof count !== 'number' || !Number.isFinite(count)) return 'View results';
    return 'View ' + count + (count === 1 ? ' result' : ' results');
  }

  /** Focus-trap step: the index to focus next, or -1 to let the browser move focus. */
  function wrapFocusIndex(index, total, backwards) {
    if (total <= 0) return -1;
    if (index === -1) return backwards ? total - 1 : 0;
    if (!backwards && index === total - 1) return 0;
    if (backwards && index === 0) return total - 1;
    return -1;
  }

  /** Each next() aborts the previous request's signal, so only the latest response is used. */
  function createRequestGate() {
    let controller = null;
    return {
      next() {
        if (controller) controller.abort();
        controller = new AbortController();
        return controller.signal;
      },
      cancel() {
        if (controller) controller.abort();
        controller = null;
      },
    };
  }

  /** A small least-recently-used cache for section HTML, keyed by page URL. */
  function createResponseCache(limit) {
    const entries = new Map();
    return {
      get(key) {
        if (!entries.has(key)) return undefined;
        const value = entries.get(key);
        entries.delete(key);
        entries.set(key, value);
        return value;
      },
      set(key, value) {
        entries.delete(key);
        entries.set(key, value);
        while (entries.size > limit) entries.delete(entries.keys().next().value);
      },
    };
  }

  function debounce(fn, wait) {
    let timer = null;
    function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn(...args);
      }, wait);
    }
    debounced.cancel = () => {
      clearTimeout(timer);
      timer = null;
    };
    return debounced;
  }

  // ---- Browser behaviour ----

  const SWAP_SELECTOR = '[data-facets-swap]';

  function headerBottom() {
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-bottom'));
    return Number.isFinite(value) ? value : 90;
  }

  function formPath(form) {
    return new URL(form.getAttribute('action') || window.location.pathname, window.location.href).pathname;
  }

  function isPlainClick(event) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented;
  }

  function isAbort(error, signal) {
    return (signal && signal.aborted) || (error && error.name === 'AbortError');
  }

  /**
   * Which window.location method a failed section refresh should fall back
   * to. A popstate-triggered refresh (push === false) has already had its
   * URL applied by the browser's own Back/Forward traversal, so falling back
   * with `assign` would stack a second entry on top of it; `replace` reloads
   * in place instead. A refresh that pushed its own URL (push === true, e.g.
   * a normal filter change) still uses `assign` so Back continues to work.
   */
  function fallbackNavigationMethod(push) {
    return push ? 'assign' : 'replace';
  }

  function createController(root) {
    const sectionId = root.getAttribute('data-section-id');
    const cache = createResponseCache(20);
    const gate = createRequestGate();
    const swapListeners = [];

    function currentUrl() {
      return window.location.pathname + window.location.search;
    }

    function currentCount() {
      const count = root.querySelector('[data-facets-swap="count"]');
      const value = count ? Number(count.getAttribute('data-results-count')) : NaN;
      return Number.isFinite(value) ? value : null;
    }

    function fetchSection(url, signal) {
      const cached = cache.get(url);
      if (cached !== undefined) return Promise.resolve(cached);
      return fetch(withSectionId(url, sectionId), { signal, credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Section request failed: ' + response.status);
          return response.text();
        })
        .then((html) => {
          cache.set(url, html);
          return html;
        });
    }

    /** Focus fallback for a control that has no id to restore focus by (chips,
     * Clear all): the results count, marked focusable in snippets/facets.liquid. */
    function focusFallback() {
      const el = root.querySelector('[data-facets-swap="count"]');
      if (el) el.focus({ preventScroll: true });
    }

    function swap(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const active = document.activeElement;
      const focusId = active && root.contains(active) ? active.id : '';
      // Chips and Clear all have no id (their url_to_remove/clear_url differ
      // per state), so track the focused one's position in the chips list
      // instead: after the swap, focus whatever now sits at that position.
      const chipsWrap = root.querySelector('[data-facets-swap="chips"]');
      let chipIndex = -1;
      if (!focusId && chipsWrap && active && chipsWrap.contains(active)) {
        chipIndex = Array.from(chipsWrap.querySelectorAll('a')).indexOf(active);
      }
      const openState = new Map();
      root.querySelectorAll('details[id]').forEach((details) => openState.set(details.id, details.open));
      const scrollState = new Map();
      root.querySelectorAll('[data-facets-scroll][id]').forEach((el) => scrollState.set(el.id, el.scrollTop));

      root.querySelectorAll(SWAP_SELECTOR).forEach((target) => {
        const key = target.getAttribute('data-facets-swap');
        const source = doc.querySelector('[data-facets-swap="' + key + '"]');
        if (!source) return;
        target.hidden = source.hidden;
        if (target.hasAttribute('data-facets-live')) {
          if (target.textContent !== source.textContent) target.textContent = source.textContent;
          target.setAttribute('data-results-count', source.getAttribute('data-results-count') || '');
        } else {
          target.innerHTML = source.innerHTML;
        }
      });

      root.querySelectorAll('a[href*="section_id="]').forEach((link) => {
        link.setAttribute('href', removeParams(link.getAttribute('href'), ['section_id']));
      });
      root.querySelectorAll('details[id]').forEach((details) => {
        if (openState.has(details.id)) {
          details.open = openState.get(details.id) || details.hasAttribute('data-facets-keep-open');
        }
      });
      scrollState.forEach((top, id) => {
        const el = document.getElementById(id);
        if (el) el.scrollTop = top;
      });
      if (focusId) {
        const el = document.getElementById(focusId);
        if (el && el !== document.activeElement) el.focus({ preventScroll: true });
      } else if (chipIndex !== -1) {
        const newChipsWrap = root.querySelector('[data-facets-swap="chips"]');
        const items = newChipsWrap ? Array.from(newChipsWrap.querySelectorAll('a')) : [];
        const next = items[chipIndex] || items[items.length - 1];
        if (next) next.focus({ preventScroll: true });
        else focusFallback();
      }
      const grid = root.querySelector('[data-facets-swap="grid"]');
      if (grid && typeof window.initProductCardVideos === 'function') window.initProductCardVideos(grid);
      swapListeners.forEach((listener) => listener());
    }

    function revealResults() {
      const anchor = root.querySelector('[data-facets-anchor]');
      if (!anchor) return;
      const offset = headerBottom() + 16;
      const top = anchor.getBoundingClientRect().top;
      if (top < offset) window.scrollTo(0, window.scrollY + top - offset);
    }

    function navigate(url, options) {
      const push = !options || options.push !== false;
      const reveal = !options || options.reveal !== false;
      const signal = gate.next();
      root.setAttribute('aria-busy', 'true');
      return fetchSection(url, signal)
        .then((html) => {
          if (signal.aborted) return false;
          swap(html);
          if (push) window.history.pushState({ facets: true }, '', url);
          if (reveal) revealResults();
          return true;
        })
        .catch((error) => {
          if (isAbort(error, signal)) return false;
          window.location[fallbackNavigationMethod(push)](url);
          return false;
        })
        .finally(() => {
          if (!signal.aborted) root.removeAttribute('aria-busy');
        });
    }

    return {
      root,
      sectionId,
      currentUrl,
      currentCount,
      fetchSection,
      navigate,
      onSwap(listener) {
        swapListeners.push(listener);
      },
    };
  }

  function wireDesktop(controller) {
    const root = controller.root;

    function apply(form) {
      controller.navigate(buildUrl(formPath(form), new FormData(form)));
    }

    root.addEventListener('change', (event) => {
      const form = event.target.form;
      if (form && form.getAttribute('data-facets-form') === 'desktop') apply(form);
    });

    root.addEventListener('submit', (event) => {
      const form = event.target;
      if (form.getAttribute('data-facets-form') !== 'desktop') return;
      event.preventDefault();
      apply(form);
    });

    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-facets-link]');
      if (!link || !isPlainClick(event)) return;
      event.preventDefault();
      controller.navigate(removeParams(link.getAttribute('href'), ['page', 'section_id']));
    });

    window.addEventListener('popstate', () => {
      if (!root.isConnected) return;
      controller.navigate(controller.currentUrl(), { push: false, reveal: false });
    });
  }

  // ---- Mobile sheet ----

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function wireSheet(controller) {
    const root = controller.root;
    const sheet = root.querySelector('[data-facets-sheet]');
    const openButton = root.querySelector('[data-facets-open]');
    if (!sheet || !openButton) return;
    const prefetchGate = createRequestGate();
    const desktop = window.matchMedia('(min-width: 1024px)');
    let stagedUrl = null;

    const sheetForm = () => sheet.querySelector('form[data-facets-form="sheet"]');
    const viewButton = () => sheet.querySelector('[data-facets-view]');

    function setView(count, busy) {
      const button = viewButton();
      if (!button) return;
      button.textContent = viewLabel(count);
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    const prefetch = debounce((url) => {
      const signal = prefetchGate.next();
      controller
        .fetchSection(url, signal)
        .then((html) => {
          if (!signal.aborted && url === stagedUrl) setView(readResultsCount(html), false);
        })
        .catch((error) => {
          if (!isAbort(error, signal)) setView(null, false);
        });
    }, 300);

    function stopPrefetch() {
      prefetch.cancel();
      prefetchGate.cancel();
    }

    function stage() {
      const form = sheetForm();
      if (!form) return;
      stagedUrl = buildUrl(formPath(form), new FormData(form));
      const button = viewButton();
      if (button) button.setAttribute('aria-busy', 'true');
      prefetch(stagedUrl);
    }

    function discard() {
      const form = sheetForm();
      if (form) form.reset();
      stagedUrl = null;
      stopPrefetch();
      setView(controller.currentCount(), false);
    }

    function focusables() {
      return Array.from(sheet.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => el.getClientRects().length > 0);
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      const next = wrapFocusIndex(items.indexOf(document.activeElement), items.length, event.shiftKey);
      if (next === -1) return;
      event.preventDefault();
      items[next].focus();
    }

    function open() {
      discard();
      sheet.hidden = false;
      document.documentElement.classList.add('facets-sheet-open');
      openButton.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', onKeydown);
      const first = focusables()[0];
      if (first) first.focus();
    }

    function close(discardChanges) {
      if (sheet.hidden) return;
      if (discardChanges) discard();
      else stopPrefetch();
      sheet.hidden = true;
      document.documentElement.classList.remove('facets-sheet-open');
      openButton.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onKeydown);
      if (openButton.getClientRects().length) openButton.focus();
    }

    function reset() {
      const form = sheetForm();
      if (!form) return;
      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
      form.querySelectorAll('input[type="radio"][name="' + PRICE_PRESET_PARAM + '"]').forEach((input) => {
        input.checked = input.value === '';
      });
      const defaultSort = form.getAttribute('data-default-sort') || '';
      form.querySelectorAll('input[type="radio"][name="sort_by"]').forEach((input) => {
        input.checked = input.value === defaultSort;
      });
      stage();
    }

    function apply() {
      const form = sheetForm();
      const url = stagedUrl || (form ? buildUrl(formPath(form), new FormData(form)) : controller.currentUrl());
      stopPrefetch();
      const button = viewButton();
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }
      return controller
        .navigate(url)
        .then(() => close(false))
        .finally(() => {
          if (button) {
            button.removeAttribute('aria-busy');
            button.disabled = false;
          }
        });
    }

    openButton.addEventListener('click', open);
    sheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-facets-close]')) close(true);
      else if (event.target.closest('[data-facets-reset]')) reset();
    });
    root.addEventListener('change', (event) => {
      const form = event.target.form;
      if (form && form.getAttribute('data-facets-form') === 'sheet') stage();
    });
    root.addEventListener('submit', (event) => {
      if (event.target.getAttribute('data-facets-form') !== 'sheet') return;
      event.preventDefault();
      apply();
    });
    controller.onSwap(() => {
      // A swap can land while the sheet is open (e.g. an in-flight chip
      // removal): the sheet body is swapped too, re-rendering it to match
      // the new current state, but any stagedUrl computed before the swap no
      // longer matches what the sheet now shows. Drop it so View re-reads
      // the (now current) form instead of re-applying a stale choice.
      stagedUrl = null;
      stopPrefetch();
      setView(controller.currentCount(), false);
    });
    desktop.addEventListener('change', (event) => {
      if (event.matches) close(true);
    });
    window.addEventListener('popstate', () => close(true));
  }

  /**
   * Availability is either-or (client tweak 2026-10-04): ticking In stock unticks
   * Out of stock and vice versa. Registered before the desktop and sheet
   * listeners, so they read the form after the other option is cleared.
   */
  function wireExclusiveSwitches(root) {
    root.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.classList || !input.classList.contains('facets-switch__input') || !input.checked) return;
      const group = input.closest('.facets-switch');
      if (!group) return;
      group.querySelectorAll('.facets-switch__input').forEach((other) => {
        if (other !== input) other.checked = false;
      });
    });
  }

  function initFacets(root) {
    if (root.getAttribute('data-facets-ready') === 'true') return;
    const controller = createController(root);
    wireExclusiveSwitches(root);
    wireDesktop(controller);
    wireSheet(controller);
    root.setAttribute('data-facets-ready', 'true');
  }

  if (typeof document !== 'undefined') {
    const start = () => document.querySelectorAll('[data-facets-root]').forEach(initFacets);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('[data-facets-root]').forEach(initFacets);
    });
  }

  // ---- Node export ----
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      PRICE_PRESET_PARAM,
      parsePricePreset,
      buildQuery,
      buildUrl,
      removeParams,
      withSectionId,
      readResultsCount,
      viewLabel,
      wrapFocusIndex,
      fallbackNavigationMethod,
      createRequestGate,
      createResponseCache,
      debounce,
    };
  }
})();
